import type { FormField } from '@shared/components/add-form/add-form.component';
import type { PurchaseOrderDetailItem } from './purchase-order-add.config';

export type ClassifiedPurchaseOrderSaveError =
  | { kind: 'duplicate_series'; seriesNumber: string }
  | { kind: 'delivery_date' }
  | { kind: 'generic' };

export function classifyPurchaseOrderSaveError(
  error: any,
): ClassifiedPurchaseOrderSaveError {
  let errorResponse: any = error?.error ?? {};
  let errorString = '';

  if (typeof errorResponse === 'string') {
    errorString = errorResponse;
    try {
      errorResponse = JSON.parse(errorResponse);
    } catch {
      errorResponse = {};
    }
  }

  if (typeof errorResponse === 'object' && errorResponse !== null) {
    errorString = JSON.stringify(errorResponse);
  } else if (typeof errorResponse === 'string') {
    errorString = errorResponse;
  }

  const lowerErrorString = errorString.toLowerCase();
  const errorMessage = errorResponse?.message || errorResponse?.error || '';
  const lowerErrorMessage = String(errorMessage).toLowerCase();

  if (
    (lowerErrorString.includes('series') &&
      lowerErrorString.includes('has already existed')) ||
    (lowerErrorMessage.includes('series') &&
      lowerErrorMessage.includes('has already existed'))
  ) {
    let seriesNumber = '';

    if (errorMessage) {
      const seriesMatch = errorMessage.match(/series\s+([A-Z0-9\-]+)/i);
      if (seriesMatch?.[1]) {
        seriesNumber = seriesMatch[1];
      } else {
        const fallbackMatch = errorMessage.match(
          /([A-Z]{1,3}[0-9]{8,}[-]?[0-9]*)/i,
        );
        if (fallbackMatch?.[1]) {
          seriesNumber = fallbackMatch[1];
        }
      }
    }

    if (!seriesNumber && errorString) {
      const stringMatch = errorString.match(/series\s+([A-Z0-9\-]+)/i);
      if (stringMatch?.[1]) {
        seriesNumber = stringMatch[1];
      }
    }

    return { kind: 'duplicate_series', seriesNumber };
  }

  if (
    lowerErrorString.includes('deliverydate') &&
    lowerErrorString.includes('must be future or present date')
  ) {
    return { kind: 'delivery_date' };
  }

  if (
    errorResponse &&
    typeof errorResponse === 'object' &&
    errorResponse.deliveryDate &&
    typeof errorResponse.deliveryDate === 'string' &&
    errorResponse.deliveryDate
      .toLowerCase()
      .includes('must be future or present date')
  ) {
    return { kind: 'delivery_date' };
  }

  return { kind: 'generic' };
}

export type PurchaseOrderAddValidationIssue =
  | { kind: 'missing_required'; missingControlNames: string[] }
  | { kind: 'price_required' }
  | { kind: 'detail_items_required' };

export function evaluatePurchaseOrderAddValidation(params: {
  formData: any;
  detailItems: PurchaseOrderDetailItem[];
  formFields: FormField[];
  isPurchaseRequestMode: boolean;
}): PurchaseOrderAddValidationIssue | null {
  const { formData, detailItems, formFields, isPurchaseRequestMode } = params;

  const missingRequiredFields: string[] = [];
  formFields.forEach((field) => {
    if (field.required) {
      const fieldValue = formData?.[field.controlName];
      if (
        !fieldValue ||
        (Array.isArray(fieldValue) && fieldValue.length === 0)
      ) {
        missingRequiredFields.push(field.controlName);
      }
    }
  });

  if (missingRequiredFields.length > 0) {
    return { kind: 'missing_required', missingControlNames: missingRequiredFields };
  }

  if (isPurchaseRequestMode) {
    const itemsWithoutPrice = detailItems.filter(
      (item) => !item.unitPrice || item.unitPrice <= 0,
    );
    if (itemsWithoutPrice.length > 0) {
      return { kind: 'price_required' };
    }
  }

  if (detailItems.length === 0) {
    return { kind: 'detail_items_required' };
  }

  return null;
}

export function hasDetailUnitPriceBelowMinimum(
  items: Pick<PurchaseOrderDetailItem, 'unitPrice'>[],
  minimum = 100,
): boolean {
  return items.some((item) => (Number(item.unitPrice) || 0) < minimum);
}

export function normalizeDeliveryDateField(formData: any): any {
  const processedFormData = { ...formData };
  if (processedFormData.deliveryDate instanceof Date) {
    const year = processedFormData.deliveryDate.getFullYear();
    const month = String(
      processedFormData.deliveryDate.getMonth() + 1,
    ).padStart(2, '0');
    const day = String(processedFormData.deliveryDate.getDate()).padStart(
      2,
      '0',
    );
    processedFormData.deliveryDate = `${year}-${month}-${day}`;
  }
  return processedFormData;
}

export function extractPurchaseRequestId(
  value: any,
  purchaseRequestOptions: { label: string; value: number }[],
  purchaseRequestId: number | null,
): number | null {
  if (!value) {
    return null;
  }

  if (typeof value === 'number') {
    return value;
  }

  if (typeof value === 'string') {
    const prOption = purchaseRequestOptions.find((opt) => opt.label === value);
    if (prOption) {
      if (prOption.value === purchaseRequestId) {
        return null;
      }
      return prOption.value;
    }

    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  return null;
}

export function shouldSwitchToNormalMode(
  newPurchaseRequestId: number | null,
  purchaseRequestId: number | null,
  isPurchaseRequestMode: boolean,
): boolean {
  const wasPRSelected = purchaseRequestId !== null;
  const isPRCleared = !newPurchaseRequestId && wasPRSelected;
  return (
    isPRCleared || (!newPurchaseRequestId && isPurchaseRequestMode)
  );
}

export function shouldSwitchToPRMode(
  newPurchaseRequestId: number | null,
  purchaseRequestId: number | null,
): boolean {
  return (
    newPurchaseRequestId !== null &&
    newPurchaseRequestId !== purchaseRequestId
  );
}
